/* eslint-disable no-extra-boolean-cast */
import { NamespaceProvider, NamespaceConsumer, withNamespace } from '@ux/namespace-component';
import UtilityBar from '@ux/utility-bar';
import SalesFooter from '@ux/sales-footer';
import HeaderNavigation from '@ux/header-navigation';
import AbstractHeader from '@ux/abstract-header';
import { events } from '@ux/header-util';
import React from 'react';
import PropTypes from 'prop-types';
import LeftNavHeading from './left-nav-heading.js';
import { URL } from '@ux/util';
import memoizeOne from 'memoize-one';

/**
 * @typedef {object} Item
 * @prop {string|React.ReactElement} [title] The label of the item (may also be specified as caption)
 * @prop {string} [caption] The label of the item (may also be specified as title)
 * @prop {string} [href] The URL to link the item to
 * @prop {React.MouseEventHandler} [onClick] A click event handler to run when the item is clicked
 * @prop {string} [className] A CSS class name to apply to the item
 * @prop {string} [target] The HTML target value to apply to the item
 * @prop {string} [id] A unique identifier for the item
 * @prop {string} [eid] The analytics EID to apply to the item
 * @prop {string} [icon] An icon name to use alongside the item label
 * @prop {boolean} [active] Whether the item is currently active
 * @prop {object} [dataAttrs] A set of data-* attributes to apply to the item
 * @prop {boolean} [mobileOnly] Whether the item should only show in a mobile view (and thus excluded from this nav)
 * @prop {Item[]} [children] An array of children Items
 */

/**
 * Return a namespace if the environment variable is set, 'polyfilled' by WebPack.
 *
 * @returns {Object} Configured namespace.
 * @api private
 */
function namespace() {
  return {
    namespace: process.env.NAMESPACE // eslint-disable-line no-process-env
  };
}

/**
 * Render and expose the Header as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced Header.
 * @api public
 */
class Header extends AbstractHeader {
  constructor(props) {
    super(...arguments);

    const { components = {} } = props;
    this.state = {
      ...this.state,
      unauthenticated: this.isUnauthenticated(components.utilityBar),
      backLink: props.backLink || null,
      CartComponent: props.CartComponent || null,
      accountTrayVariant: false
    };

    this.updateCartComponent = this.updateCartComponent.bind(this);
    this.getCustomerState = this.getCustomerState.bind(this);
    this.updateBackLink = this.updateBackLink.bind(this);
  }

  /**
   * Helper to update back link.
   *
   * @param {Object} data Updated back link object.
   * @param {Function} done Continuation to call when complete.
   * @api public
   */
  updateBackLink(data, done = () => {}) {
    this.setState({
      backLink: data
    }, done);
  }

  /**
   * Helper to update cart component.
   *
   * @param {React.ReactElement} component Updated React component.
   * @param {Function} done Continuation to call when complete.
   * @api public
   */
  updateCartComponent(component, done = () => {}) {
    this.setState({
      CartComponent: component
    }, done);
  }

  render() {
    return (
      <NamespaceProvider { ...namespace() } >
        <NamespacedHeaderComponent
          { ...this.props } { ...this.state }
          getCustomerState={ this.getCustomerState }
          customer={ this.customer }
          renderBanner={ this.renderBanner.bind(this, this.props) }
        />
      </NamespaceProvider>
    );
  }
}

Header.propTypes = {
  navigation: PropTypes.array.isRequired,
  currentPage: PropTypes.string,
  preset: PropTypes.string,
  supportContacts: PropTypes.object,
  ProductNav: PropTypes.elementType,
  traffic: PropTypes.string,
  split: PropTypes.string,
  showSubCategory: PropTypes.bool,
  showSubNav: PropTypes.bool,
  subNavIndices: PropTypes.object,
  market: PropTypes.string.isRequired,
  privateLabelId: PropTypes.number.isRequired
};

Header.defaultProps = {
};

class HeaderComponent extends NamespaceConsumer {
  constructor() {
    super(...arguments);

    this.getCartProps = this.getCartProps.bind(this);
    this.allNavItems = this.props.navigation.concat(this.props.discoverNav);
    this.memoizedGetCurrentHref = memoizeOne((currentHrefFromProps, isBrowser, documentHref) => HeaderComponent._getCurrentHrefInternal(currentHrefFromProps, isBrowser, documentHref));
  }

  /**
   * Updates navigation without using state.
   */
  updateNavigation() {
    // because window.location might change
    this.forceUpdate();
  }

  /**
   * Add eventListeners for breakpoints.
   *
   * @api private
   */
  componentDidMount() {
    // This is needed until https://github.com/preactjs/preact/issues/2488 is fixed.
    // Calling `setState` (which `breakpoint` does) in `componentDidMount` breaks
    // preacts implementation of `Suspend` (which is used by `AbstractHeader.renderBanner`)
    setTimeout(() => {
      this.breakpoint('tablet', 'desktop');
    }, 0);
  }

  /**
   * Remove eventListeners for breakpoints.
   *
   * @api private
   */
  componentWillUnmount() {
    this.breakup();
  }

  getCartProps(customerState) {
    const { props } = this;
    const items = customerState.items;
    let prefix = 'uxp.hyd.sales_header.';
    let cartEid = 'cart_empty.link.click';
    if (props.seechange) {
      prefix = 'uxp.hyd.sales_header_seechange.';
    }
    if (items > 0) {
      cartEid = 'cart_full.link.click';
    }
    cartEid = prefix + cartEid;

    return {
      items,
      market: 'en-US',
      messages: props.messages,
      checkout: props.urls.checkout.href,
      eid: cartEid,
      className: this.namespace('cart-link'),
      showEmpty: true,
      textOnly: false
    };
  }

  /**
   * Compares two URLs, ignoring any trailing slashes.
   *
   * @param {any} url1 URL to check.
   * @param {any} url2 URL to compare.
   * @param {Boolean} [ignoreQueryStrings=false] Whether to ignore query strings.
   * @returns {Boolean} Whether URLs match
   */
  urlMatches(url1, url2, ignoreQueryStrings = false) {
    if (typeof url1 !== 'string' || typeof url2 !== 'string') {
      return false;
    }

    if (ignoreQueryStrings) {
      url1 = new URL(url1, true).set('query', {}).href;
      url2 = new URL(url2, true).set('query', {}).href;
    }

    return url1.replace(/\/$/, '') === url2.replace(/\/$/, '');
  }

  /**
    * Get the current page href for comparison of active item
    * @returns {String} current href
    * @private
    */
  getCurrentHref() {
    return this.memoizedGetCurrentHref(this.props.currentHref, this.isBrowser, this.getDocumentLocation());
  }

  /**
   * Gets document location.
   *
   * @returns {String} current location or null if run on the server side
   * @private
   */
  getDocumentLocation() {
    if (typeof window !== 'undefined') {
      return document && document.location && document.location.href;
    }
    return null;
  }

  /**
    * Get the current page href for comparison of active item
    * @param  {Boolean} currentHrefFromProps href from props
    * @param  {Boolean} isBrowser if isBrowser
    * @param  {String}  documentHref from document.location.href
    * @returns {String} current href
    * @private
    */
  static _getCurrentHrefInternal(currentHrefFromProps, isBrowser, documentHref) {
    //
    // Logic originally from
    // https://github.secureserver.net/UX/product-nav/blob/119ebd4ffc55e41a4bfebe1c973f55de0c3e35bc/lib/product-nav.js#L37
    //
    let currentHref = currentHrefFromProps;

    if (!currentHref && isBrowser) {
      currentHref = documentHref;
    }

    if (currentHref) {
      const currentUrl = new URL(currentHref, true);
      const qsCurrentHref = currentUrl.query.currentPage;
      if (qsCurrentHref) {
        currentHref = qsCurrentHref;
      } else {
        // Remove query string for comparisons
        currentUrl.set('query', {});
        currentHref = currentUrl.href;
      }
    }

    return currentHref;
  }

  /**
   * Renders the leftnav if there is one to display.
   * @param  {Number} options.subcategoryIndex
   * index for the active subcategory from sitecore data
   * @param  {Number} options.categoryIndex
   * index for the active category from sitecore data
   * @returns {(undefined|React.ReactElement)} The LeftNavHeading to render if there is one.
   */
  renderLeftNav({ subcategoryIndex, categoryIndex }) {
    if (categoryIndex === false || categoryIndex >= this.allNavItems.length) { return; }
    const categoryObj = this.allNavItems[categoryIndex];
    const categoryHref = categoryObj.href || categoryObj.leftnav && categoryObj.leftnav.href;
    const activeCatBorder = this.urlMatches(categoryHref, this.getCurrentHref(), true);
    const subcategoryObj = categoryObj.children &&
      subcategoryIndex < categoryObj.children.length &&
      categoryObj.children[subcategoryIndex];


    return (
      <LeftNavHeading
        showSubCategory={ this.props.showSubCategory }
        categoryHref={ categoryHref }
        categoryObj={ categoryObj }
        subcategoryObj={ subcategoryObj }
        activeCatBorder={ activeCatBorder }
        urlArgs={ this.props.urlArgs } />
    );
  }

  /**
   * Pulls items for header-nav from allNavItems (discovernav + productnav)
   * @param  {Number} options.subcategoryIndex
   * index for the active subcategory from sitecore data
   * @param  {Number} options.categoryIndex
   * index for the active category from sitecore data
   * @param {string} [currentHref] The current page URL.
   * @returns {(undefined|Object[])} Array of objects to use for header-nav
   */
  getHeaderNavItems({ categoryIndex, subcategoryIndex }, currentHref) {
    if (categoryIndex === false || categoryIndex >= this.allNavItems.length) {
      return;
    }
    const { showSubCategory } = this.props;
    const items = this.allNavItems[categoryIndex].children;

    if (subcategoryIndex || subcategoryIndex === 0 && subcategoryIndex < items.length) {
      items[subcategoryIndex].active = true;
      if (this.isBrowser && items[subcategoryIndex].children) {
        items[subcategoryIndex].children.some(child => {
          if (child.href && this.urlMatches(currentHref, child.href, true)) {
            child.active = true;
            return true;
          }
        });
      }
    }

    if ((subcategoryIndex === false) || !showSubCategory) {
      return items;
    }

    return items[subcategoryIndex].children;
  }

  render() {
    const props  = this.props;
    const {
      components,
      helpcenterGuides,
      utilityBar,
      ProductNav,
      market,
      messages,
      subNavIndices,
      eidPrefix,
      urlArgs
    } = props;

    const ubComponents = components && components.utilityBar;
    const customerState = props.getCustomerState();
    const helpLink = helpcenterGuides ? 'Sales:HelpByGoDaddyGuides' : 'Shared:Help:HeaderLink';
    const showAccountTrayComponent = props.accountTrayVariant === 'B' && !props.isReseller && customerState.loggedIn;

    return (
      // eslint-disable-next-line no-return-assign
      <div ref={ r => this.headerElement = r }>
        { this.props.renderBanner() }
        <UtilityBar
          { ...props }
          { ...utilityBar }
          { ...customerState }
          cart={ this.getCartProps(customerState) }
          fullHelpLinkId={ helpLink }
          components={ ubComponents }
          showAccountTrayComponent={ showAccountTrayComponent } />
        { ProductNav && <ProductNav { ...props } components={ components && components.productNav } /> }
        { props.showSubNav && <div className={ this.namespace('sub-nav-border') }></div> }
        { props.showSubNav && <div className={ this.namespace('sub-nav-wrap') }>
          { this.renderLeftNav(subNavIndices) }
          <HeaderNavigation
            messages={ messages }
            market={ market }
            eidPrefix={ eidPrefix }
            moreMenuEid={ `${eidPrefix}_more.menu.click` }
            items={ (this.getHeaderNavItems(subNavIndices, this.getCurrentHref()) || []).concat() }
            urlArgs={ urlArgs }
            scale />
        </div>
        }
      </div>
    );
  }
}

HeaderComponent.propTypes = {
  eidPrefix: PropTypes.string,
  urlArgs: PropTypes.object
};

HeaderComponent.defaultProps = {
  eidPrefix: 'uxp.hyd.subnav'
};

const NamespacedHeaderComponent = withNamespace(HeaderComponent);

class Footer extends React.Component {
  componentDidMount() {
    events.emit('mount', 'footer', this);
    events.emit('mount:footer', this);
  }

  render() {
    const {
      ResellerSalesFooter,
      ...props
    } = this.props;

    const footer = ResellerSalesFooter
      ? <ResellerSalesFooter { ...props } />
      : <SalesFooter { ...props } seechange={ true } />;

    return (
      <NamespaceProvider { ...namespace() } >
        { footer }
      </NamespaceProvider>
    );
  }
}

Footer.propTypes = {
  ResellerSalesFooter: PropTypes.elementType
};

Footer.defaultProps = {
};

export {
  Header,
  HeaderComponent,
  Footer
};
